import { useMemo, useState, ReactNode } from 'react';
import { getRouteApi, Link as RouterLink } from '@tanstack/react-router';
import GoBackHeader from '@emfood/shared/src/components/go-back-header/go-back-header';
import {
  FillHeight,
  createSortSchema,
  ACHelmetContainer,
  createPaginationSchema,
  useSyncTableSearchParams,
  getTableModelsFromSearchParams,
} from '@emfood/shared';

import { DataGrid, GridColDef, GridSortModel, GridPaginationModel } from '@mui/x-data-grid';
import {
  Card,
  Link,
  Stack,
  Button,
  useTheme,
  Typography,
  CardContent,
  useMediaQuery,
} from '@mui/material';

import { paths } from 'src/routes/paths';

import { useGetOrderIssuesLog } from 'src/api/hooks/api-hooks';
import { OrderIssueType, ProductIssueType, OrderIssueLogModel } from 'src/api/models/orders';

import { useSettingsContext } from 'src/components/settings';
import IssueStatus from 'src/components/issue-log/issue-status';

import IssueCard from './issue-card';

export const IssueLogSearchParamsSchema = createPaginationSchema().concat(
  createSortSchema([
    'createdOn',
    'distributorName',
    'orderReferenceNumber',
    'productName',
    'issueType',
  ] as const)
);

const routeApi = getRouteApi('/issues/');

export default function IssueLog() {
  const navigate = routeApi.useNavigate();
  const routeSearch = routeApi.useSearch();

  const { paginationModel: searchPaginationModel, sortModel: searchSortModel } =
    getTableModelsFromSearchParams(routeSearch);

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(
    searchPaginationModel ?? { pageSize: 25, page: 0 }
  );
  const [sortModel, setSortModel] = useState<GridSortModel>(searchSortModel ?? []);

  useSyncTableSearchParams({ navigate, paginationModel, sortModel });

  const { data: issues } = useGetOrderIssuesLog(
    { pageSize: paginationModel.pageSize, pageIndex: paginationModel.page },
    sortModel[0]
      ? {
          field: sortModel[0]?.field,
          sort: sortModel[0]?.sort || undefined,
        }
      : undefined
  );

  const settings = useSettingsContext();
  const columns: GridColDef<OrderIssueLogModel>[] = useMemo(
    () => [
      {
        field: 'createdOn',
        headerName: 'Date Created',
        valueGetter: (params) => {
          const date = new Date(params.row.createdOn);
          return date.toLocaleDateString();
        },
        minWidth: 120,
      },
      {
        field: 'distributorName',
        headerName: 'Supplier',
        valueGetter: (params) => params.row.distributorName || 'N/A',
        minWidth: 120,
      },
      {
        field: 'orderReferenceNumber',
        headerName: 'PO Number',
        renderCell: (params) => (
          <Link component={RouterLink} to={paths.dashboard.orders.orderDetails(params.row.orderId)}>
            {params.value}
          </Link>
        ),
        minWidth: 100,
      },
      {
        field: 'productName',
        headerName: 'Product Name',
        renderCell: (params) => {
          if (!params.row.productName) return 'Whole Order';
          return (
            <Stack overflow="hidden">
              <Typography
                title={params.row.productName}
                textOverflow="ellipsis"
                overflow="hidden"
                variant="body2"
                color="textPrimary"
              >
                {params.row.productName}
              </Typography>
              <Typography variant="body2" color="GrayText">
                {params.row.units}
              </Typography>
            </Stack>
          );
        },
        flex: 1,
        minWidth: 120,
      },
      {
        field: 'issueType',
        headerName: 'Issue Type',
        valueGetter: (params) =>
          OrderIssueType[Number(params.row.issueType)] ||
          ProductIssueType[Number(params.row.issueType)],
        minWidth: 150,
      },
      {
        field: 'status',
        headerName: 'Status',
        sortable: false,
        renderCell: (params) => <IssueStatus issue={params.row} />,
        minWidth: 280,
      },
      {
        field: 'actions',
        headerName: 'Actions',
        sortable: false,
        minWidth: 120,
        renderCell: (params) => (
          <Button
            variant="outlined"
            component={RouterLink}
            to={paths.dashboard.issues.issueDetails(params.row.orderId, params.row.issueId)}
          >
            Issue Details
          </Button>
        ),
      },
    ],
    []
  );

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const mobileComponent = useMemo<ReactNode>(
    () => (
      <Stack spacing={2}>
        {issues?.data?.map((issue) => (
          <IssueCard key={issue.issueId} issue={issue} />
        ))}
      </Stack>
    ),
    [issues?.data]
  );

  return (
    <ACHelmetContainer title="Issue Log" maxWidth={settings.themeStretch ? false : 'xl'}>
      <GoBackHeader heading="Issue Log" disableGoBack />
      <Card>
        <CardContent>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Below is a list of past issues you have logged for products or entire orders.
          </Typography>
          {isMobile ? (
            mobileComponent
          ) : (
            <FillHeight>
              <DataGrid
                rows={issues?.data || []}
                columns={columns}
                rowCount={issues?.totalRecords || 0}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                sortingMode="server"
                sortModel={sortModel}
                rowHeight={60}
                onSortModelChange={setSortModel}
                paginationMode="server"
                pageSizeOptions={[25]}
                disableRowSelectionOnClick
                disableColumnMenu
                getRowId={(row) => row.issueId}
              />
            </FillHeight>
          )}
        </CardContent>
      </Card>
    </ACHelmetContainer>
  );
}
